'use client'

import { Spinner } from '../Progress'
import { AccessibleOverlayProps, useAccessibleOverlay } from '../../../hooks/useAccessibleOverlay'
import { TranslatedText } from '../../../types/TranslatedText'
import React, { ReactElement, ReactNode, isValidElement, useCallback, useId, useRef, useState } from 'react'
import { PlacesType, Tooltip, TooltipRefProps } from 'react-tooltip'
import 'react-tooltip/dist/react-tooltip.css'

let activeHoverPopoverClose: (() => void) | null = null

/** Closes the previous popover (that was opened on hover!) before opening the new one. */
const closeOtherHoverPopoversBeforeOpen = (thisInstanceClose: () => void) => {
    const previous = activeHoverPopoverClose
    if (previous && previous !== thisInstanceClose) {
        previous()
    }
    activeHoverPopoverClose = thisInstanceClose
}

/** Clears the hover popover if the current instance is the one that was opened on hover. */
const clearHoverPopoverIfCurrent = (thisInstanceClose: () => void) => {
    if (activeHoverPopoverClose === thisInstanceClose) {
        activeHoverPopoverClose = null
    }
}

export type PopoverWidth = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

export interface PopoverProps
    extends Omit<TooltipRefProps, 'variant' | 'open' | 'close' | 'activeAnchor' | 'isOpen' | 'place'>,
        AccessibleOverlayProps {
    id?: string
    children?: ReactNode
    popoverContent: ReactNode
    buttons?: ReactNode
    delayHide?: number
    /** When openOnHover is true: popover opens only after the pointer has stayed on the trigger for this many ms. Quick pass-over does not open. */
    delayShow?: number
    isLoading?: boolean
    underlined?: boolean
    className?: string
    minWidth?: PopoverWidth
    maxWidth?: PopoverWidth
    place?: PlacesType
    /** Accessible label for the trigger when it has no visible text (e.g. icon-only). Rendered with sr-only. */
    screenReaderLabel?: TranslatedText
    /** When true (default), popover opens on both click and hover. Set false for click-only (e.g. strict keyboard accessibility). */
    openOnHover?: boolean
}

const isTriggerElement = (
    child: ReactNode,
): child is React.ReactElement<React.ButtonHTMLAttributes<HTMLButtonElement> & { href?: string }> =>
    isValidElement(child) && (child.type === 'button' || child.type === 'a')

export const Popover = ({
    id,
    children,
    buttons,
    popoverContent,
    className = '',
    isLoading = false,
    delayHide = 0,
    delayShow = 500,
    underlined = true,
    minWidth = 'sm',
    maxWidth = 'lg',
    place = 'top',
    screenReaderLabel,
    openOnHover = true,
    ariaLabel,
    ariaLabelledBy,
    trapFocus = true,
    ...rest
}: PopoverProps): ReactElement => {
    const reactId = useId()
    const uniqueId = id ?? reactId
    const contentId = `${uniqueId}-content`
    const [isOpen, setIsOpen] = useState(false)
    const tooltipRef = useRef<TooltipRefProps>(null)
    const hoverShowAbortRef = useRef<AbortController | null>(null)

    const closeFromHoverCoordinator = useCallback(() => {
        tooltipRef.current?.close()
    }, [])

    /** Handles the mouse enter event for the trigger, so this is only called on hover, not on click */
    const handleTriggerMouseEnter = useCallback(() => {
        hoverShowAbortRef.current?.abort()
        const controller = new AbortController()
        hoverShowAbortRef.current = controller
        const timeoutId = setTimeout(() => {
            if (!controller.signal.aborted) {
                closeOtherHoverPopoversBeforeOpen(closeFromHoverCoordinator)
                tooltipRef.current?.open()
            }
            hoverShowAbortRef.current = null
        }, delayShow)
        controller.signal.addEventListener(
            'abort',
            () => clearTimeout(timeoutId),
            { once: true },
        )
    }, [closeFromHoverCoordinator, delayShow])

    /** Handles the mouse leave event for the trigger, so this is only called on hover, not on click */
    const handleTriggerMouseLeave = useCallback(() => {
        hoverShowAbortRef.current?.abort()
        hoverShowAbortRef.current = null
    }, [])

    React.useEffect(() => () => hoverShowAbortRef.current?.abort(), [])

    React.useEffect(
        () => () => {
            clearHoverPopoverIfCurrent(closeFromHoverCoordinator)
        },
        [closeFromHoverCoordinator],
    )

    const overlay = useAccessibleOverlay({
        isOpen,
        contentId,
        trapFocus,
        ariaLabel,
        ariaLabelledBy,
        role: 'dialog',
        ariaModal: false,
    })

    const hoverTriggerHandlers =
        openOnHover ?
            {
                onMouseEnter: handleTriggerMouseEnter,
                onMouseLeave: handleTriggerMouseLeave,
            }
            : null

    const triggerClassName = `${className} ${underlined ? 'underline underline-offset-2 decoration-dotted a11y-outline' : ''}`.trim()

    const triggerContent = (
        <>
            {screenReaderLabel && <span className="sr-only">{screenReaderLabel}</span>}
            {children}
        </>
    )

    /**
     * Renders the element that opens the popover. If children is a <button> or <a>, we use it
     * as the trigger and inject ARIA and tooltip props; otherwise we wrap children in a <button>
     * so the trigger is always focusable and keyboard-activatable for accessibility.
     */
    const renderTrigger = () => {
        if (!children) return null

        // ARIA and tooltip props that every trigger must have (focus return + react-tooltip anchor)
        const sharedTriggerProps = {
            ...overlay.triggerAriaProps,
            'data-tooltip-id': uniqueId,
            ...hoverTriggerHandlers,
        }

        // When children is a <button> or <a>, use it as the trigger and inject our props + ref
        if (isTriggerElement(children)) {
            const childRef = (children as React.ReactElement & { ref?: React.Ref<HTMLElement> }).ref

            // Merge refs: we need overlay.triggerRef for focus return; forward consumer ref if present
            const setRef = (el: HTMLElement | null) => {
                (overlay.triggerRef as React.MutableRefObject<HTMLElement | null>).current = el
                if (typeof childRef === 'function') (childRef as (el: HTMLElement | null) => void)(el)
                else if (childRef && typeof childRef === 'object') ((childRef as React.MutableRefObject<HTMLElement | null>).current = el)
            }

            // Return a clone of the element and inject our props + ref
            return React.cloneElement(children as React.ReactElement<React.ButtonHTMLAttributes<HTMLButtonElement> & { ref?: React.Ref<HTMLElement> }>, {
                ...sharedTriggerProps,
                ref: setRef,
                className: `${triggerClassName} ${children.props.className ?? ''}`.trim(),
                children: (
                    <>
                        {screenReaderLabel && <span className="sr-only">{screenReaderLabel}</span>}
                        {children.props.children}
                    </>
                ),
            })
        }

        // When children is not a button/anchor (e.g. <span>), wrap in a <button> so the trigger is focusable and keyboard-activatable
        return (
            <button
                type="button"
                ref={overlay.triggerRef as React.Ref<HTMLButtonElement>}
                {...sharedTriggerProps}
                className={triggerClassName}
            >
                {triggerContent}
            </button>
        )
    }

    return (
        <div className="popover">
            {children && (
                <>
                    {renderTrigger()}
                    <Tooltip
                        ref={tooltipRef}
                        {...rest}
                        place={place}
                        id={uniqueId}
                        delayHide={delayHide}
                        delayShow={0}
                        openEvents={{ click: true }}
                        closeEvents={{ click: true }}
                        globalCloseEvents={{ escape: true, clickOutsideAnchor: true }}
                        clickable
                        afterShow={() => {
                            setIsOpen(true)
                            overlay.focusContent()
                        }}
                        afterHide={() => {
                            clearHoverPopoverIfCurrent(closeFromHoverCoordinator)
                            setIsOpen(false)
                            overlay.focusTrigger()
                        }}
                        className="bg-shade-100 rounded-lg z-50 !p-0 break-words whitespace-normal !opacity-100"
                        data-cy="popover"
                    >
                        <div
                            ref={overlay.contentRef as React.Ref<HTMLDivElement>}
                            {...overlay.contentProps}
                            className={`pointer-events-auto popover__width-max--${maxWidth} popover__width-min--${minWidth}`}
                        >
                            {isLoading ? (
                                <div className="grid place-content-center">
                                    <Spinner className="m-4" />
                                </div>
                            ) : (
                                <>
                                    <div className="p-4">{popoverContent}</div>
                                    {buttons && <div className="w-full flex justify-between gap-1">{buttons}</div>}
                                </>
                            )}
                        </div>
                    </Tooltip>
                </>
            )}
        </div>
    )
}
